import { isNil } from 'lodash'
import {
    InfiniteData,
    useInfiniteQuery,
    UseInfiniteQueryResult,
    useMutation,
    UseMutationResult,
    useQuery,
    useQueryClient,
    UseQueryResult
} from '@tanstack/react-query'
import {
    Invoice,
    UpdateInvoice,
    UpdateInvoiceAttachments,
    CreateInvoice,
    UnlinkReceivable,
    LinkReceivable
} from '../models/invoiceModels'
import {
    createInvoiceService,
    getInvoiceByIdService,
    getInvoicesService,
    linkReceivableToInvoiceService,
    unlinkReceivableFromInvoiceService,
    updateInvoiceService
} from '../services/invoicesServices'
import { RequestResponse } from '../services/requestServices'
import { getPageSize } from '../utils/queryUtils'
import { useMe } from './useMe'
import { QueryStringInput } from '@hello-aria/query-string/dist/stringify'
import { isValidUUID } from '../utils/validatorUtils'
import { State, updateAttachmentsService } from '../services/uploadServices'

export const invoicesQueryKeys = {
    root: ['invoices'],
    infiniteInvoices: (companyId: string, query?: QueryStringInput): (string | undefined | QueryStringInput)[] => [
        ...invoicesQueryKeys.root,
        companyId,
        query
    ],
    invoiceById: (invoiceId: string | undefined, companyId: string): (string | undefined)[] => [
        ...invoicesQueryKeys.root,
        companyId,
        invoiceId
    ]
}

type UseInfiniteInvoices = {
    query?: QueryStringInput
    isEnabled?: boolean
}

export const useInfiniteInvoices = (
    options?: UseInfiniteInvoices
): UseInfiniteQueryResult<InfiniteData<RequestResponse<Invoice[]> | undefined>> => {
    const { isEnabled, query } = options || {}
    const { companyId } = useMe()

    return useInfiniteQuery({
        queryKey: invoicesQueryKeys.infiniteInvoices(companyId, query),

        queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
            return getInvoicesService({
                hasAttachments: false,
                companyId,
                query: {
                    pagination: {
                        after: pageParam,
                        first: getPageSize(query)
                    },
                    ...query
                }
            })
        },

        enabled: isEnabled,

        getNextPageParam: (lastPage) => {
            if (lastPage?.pagination?.hasNextPage) return lastPage?.pagination?.endCursor
            return undefined
        },

        initialPageParam: undefined
    })
}

export const useInvoiceById = (
    invoiceId: string | undefined,
    hasAttachments = true
): UseQueryResult<RequestResponse<Invoice> | undefined> => {
    const { companyId } = useMe()
    return useQuery({
        queryKey: invoicesQueryKeys.invoiceById(invoiceId, companyId),
        queryFn: async () => {
            const response = await getInvoiceByIdService({ invoiceId, companyId, hasAttachments })

            return response
        },
        enabled: !isNil(invoiceId) && isValidUUID(invoiceId)
    })
}

export const useCreateInvoice = (): UseMutationResult<
    RequestResponse<Invoice | undefined> | undefined,
    unknown,
    CreateInvoice
> => {
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload) => {
            return createInvoiceService(companyId, payload)
        }
    })
}

export const useUpdateInvoice = (): UseMutationResult<
    RequestResponse<Invoice | undefined> | undefined,
    unknown,
    UpdateInvoice
> => {
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload) => {
            return updateInvoiceService(companyId, payload)
        }
    })
}

export const useUpdateInvoiceAttachments = (
    state: State
): UseMutationResult<RequestResponse<Invoice | undefined> | undefined, unknown, UpdateInvoiceAttachments> => {
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload) => updateAttachmentsService<Invoice>('invoice', companyId, payload, state)
    })
}

export const useInvalidateInvoices = () => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()

    const invalidateInvoices = async () => {
        await queryClient.invalidateQueries({
            queryKey: invoicesQueryKeys.infiniteInvoices(companyId)
        })
    }

    const invalidateInvoiceById = async (invoiceId: string) => {
        await queryClient.invalidateQueries({
            queryKey: invoicesQueryKeys.invoiceById(invoiceId, companyId),
            exact: true
        })
    }

    return { invalidateInvoices, invalidateInvoiceById }
}

export const useUnlinkReceivableFromInvoice = () => {
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload: UnlinkReceivable) => {
            return unlinkReceivableFromInvoiceService(companyId, payload)
        }
    })
}

export const useLinkReceivablesToInvoice = () => {
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload: LinkReceivable) => {
            return linkReceivableToInvoiceService(companyId, payload)
        }
    })
}
